import moment from 'moment';
import store from '@/store';
import { hexToRgb as hexColorToRgb } from './color';
import { set as setInIDB } from 'idb-keyval';
import { Auth } from 'aws-amplify';
import logger from '@goldcast/logger';
import { deleteAllCookies, setCookieValue } from './cookies';
import { logoutUser } from '@goldcast/api/backend';
import { trackLoginOrLogoutEvent } from '@/analytics';
import { sessionService } from '@/services/sessionService';

export function removeSpaces(str) {
  return str.replace(/\s/g, '');
}

export function isCssVar(key) {
  if (!key || typeof key !== 'string') return false;
  return key.startsWith('var(--');
}

export function extractKeyFromCssVar(cssVar) {
  let varString = cssVar.substring(6, cssVar.length - 1);
  return varString.split('-')?.join('_');
}

export function getCssVarValue(cssVar) {
  const themeObj = store.getters['branding/theme_obj'];
  const key = extractKeyFromCssVar(cssVar);
  return themeObj[key];
}

export function cognitoSignout() {
  const isGlobalSignOutDisabled = document.cookie.includes('isGlobalSignOutDisabledGCInternal=true');
  const userHasAccessToken = !!store.state.user?.userCognitoDetails?.signInUserSession?.accessToken?.jwtToken;

  const signOutPromise = isGlobalSignOutDisabled
    ? Auth.signOut()
    : (userHasAccessToken
        ? logoutUser({}).catch(err => {
            logger.error('[utils] logout API request failed', err);
            return Promise.resolve();
          })
        : Promise.resolve()
      )
        .then(() => Auth.signOut({ global: true }))
        .catch(err => {
          // When the users access token has been revoked
          // global signout does not work. Cognito suggests to do
          // a normal signout when global signout fails due to invalid
          // tokens:
          // https://github.com/aws-amplify/amplify-js/issues/3540
          //
          // Amplify auth library only tries to fetch the user details when the
          // user logouts when global signout is enabled:
          // https://github.com/aws-amplify/amplify-js/blob/c58d1e6b5cdc996c28770ef56d561006b43d5579/packages/auth/src/Auth.ts#L2075
          if (err.message === 'Access Token has been revoked') {
            return Auth.signOut();
          }
          throw err;
        });

  return signOutPromise;
}

export function safeSignout() {
  return new Promise((resolve, reject) => {
    cognitoSignout()
      .then(() => {
        trackLoginOrLogoutEvent({ eventType: 'Logout', isSuccess: true });
        sessionService.endSession();
        deleteAllCookies();
        setCookieValue('logged_in_via_sso', false);
        resolve();
      })
      .catch(err => {
        logger.error('[utils] Cognito sign-out failed', err);
        trackLoginOrLogoutEvent({ eventType: 'Logout', isSuccess: false });
        reject(err);
      });
  });
}

export const timeFormatter = new Intl.DateTimeFormat('en', {
  hour: 'numeric',
  minute: 'numeric'
});

export const dateFormatter = new Intl.DateTimeFormat('en', {
  month: 'short',
  day: 'numeric'
});

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export const datesAreOnSameDay = (first, second) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

export const startToEndDateString = (startDate, endDate) => {
  try {
    const startTimeString = timeFormatter.format(startDate);
    const endTimeString = timeFormatter.format(endDate);
    /* eslint-disable */
    return datesAreOnSameDay(startDate, endDate)
      ? `${dateFormatter.format(startDate)} · ${startTimeString} - ${endTimeString}`
      : dateFormatter.formatRange(startDate, endDate);
    /* eslint-enable */
  } catch (e) {
    return '';
  }
};

export function uuidGen() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

export const getFileBlob = file => {
  return new Promise(resolve => {
    let reader = new FileReader();
    reader.onload = function (e) {
      let blob = new Blob([new Uint8Array(e.target.result)], {
        type: file.type
      });
      resolve(blob);
    };
    reader.readAsArrayBuffer(file);
  });
};

export const resourcesAllowedFileTypes = 'image/*, .txt, .doc, .docx, .odt, .csv, .ods, .xls, .xlsx, .pdf, .ppt, .pptx';

export const destructureRgbColor = rgbColorString => {
  if (typeof rgbColorString === 'string') {
    let rgbValue = null;
    if (rgbColorString.startsWith('rgba(')) {
      rgbValue = rgbColorString;
    } else if (rgbColorString.startsWith('#')) {
      rgbValue = hexColorToRgb(rgbColorString);
    } else if (rgbColorString === 'transparent') {
      rgbValue = `rgba(0, 0, 0, 0)`;
    }

    if (rgbValue) {
      const values = rgbValue.split(/(?:rgba\()|(?:,)|(?:\))/).filter(Boolean);
      const destructuredRgb = {
        red: parseInt(values[0]),
        green: parseInt(values[1]),
        blue: parseInt(values[2]),
        alpha: parseFloat(values[3]),
        style: rgbValue
      };

      return destructuredRgb;
    }
  }
};

export const hexToRgb = hexColorToRgb;

export function isColorString(colorStr) {
  return (
    typeof colorStr === 'string' &&
    (colorStr.startsWith('rgba(') || colorStr.startsWith('#') || colorStr === 'transparent')
  );
}

export function isGradientString(gradientString) {
  return (
    typeof gradientString === 'string' &&
    (gradientString.startsWith('linear-gradient(') || gradientString.startsWith('radial-gradient('))
  );
}

export function isUrlString(urlString) {
  return typeof urlString === 'string' && (urlString.startsWith("Url('") || urlString.startsWith("url('"));
}

function getHexValue(c) {
  var hex = c?.toString(16);
  return hex?.length == 1 ? '0' + hex : hex;
}

export function getHexString(rgba) {
  if (!rgba) return '#ff0000';
  return `#${getHexValue(rgba.red)}${getHexValue(rgba.green)}${getHexValue(rgba.blue)}`;
}

export function destructureUrl(urlString) {
  if (isUrlString(urlString)) {
    const regexp = /(?:Url\(')|(?:'\))/gi;
    const values = urlString.split(regexp).filter(Boolean);
    return values[0];
  }
}

export function destructureGradientColor(gradientString) {
  if (typeof gradientString === 'string') {
    if (gradientString.startsWith('linear-gradient(')) {
      const linearGradientMatches = gradientString.match(/(\d+deg)|(rgba\([\d*.?\d*,\s?]+\))|(\d+(\.\d+)?%)/g);
      const degreeValue = linearGradientMatches?.shift()?.split('deg')[0];
      const points = [];

      for (let i = 0; i < linearGradientMatches.length; i = i + 2) {
        const destructuredRgba = destructureRgbColor(linearGradientMatches[i]);

        if (destructuredRgba) {
          destructuredRgba.left = linearGradientMatches[i + 1]?.split('%')[0];
          points.push(destructuredRgba);
        }
      }

      return {
        type: 'linear',
        style: gradientString,
        degree: parseInt(degreeValue),
        points: points
      };
    } else if (gradientString.startsWith('radial-gradient(')) {
      const regexp = /(?:rgba\([\d*.?\d*,\s?]+\))|(?:\d+(\.\d+)?%)/gi;
      const radialGradientMatches = gradientString.match(regexp);
      const radialPoints = [];

      for (let j = 0; j < radialGradientMatches.length; j = j + 2) {
        const destructuredRgb = destructureRgbColor(radialGradientMatches[j]);
        destructuredRgb.left = radialGradientMatches[j + 1]?.split('%')[0];
        radialPoints.push(destructuredRgb);
      }

      return {
        type: 'radial',
        degree: 270,
        style: gradientString,
        points: radialPoints
      };
    }
  }
}

export const processErrorParent = error => {
  try {
    JSON.parse(error.toString());
    return processError(error);
  } catch {
    return error;
  }
};

export const getRGBAstring = ({ red, green, blue, alpha }) => {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const processError = error => {
  let message = 'Unknown Error Occurred !';
  try {
    const errors = JSON.parse(error.toString());
    if (Array.isArray(errors)) {
      if (errors.length > 0) {
        message = errors[0].message;
      }
    } else {
      const errorFields = Object.keys(errors);
      if (errorFields?.length > 0) {
        const error = errors[errorFields[0]];
        if (typeof error === 'string') {
          message = errors['message'] || errors['response'];
        } else if (typeof error === 'object') {
          message = error[0].message;
        }
      }
    }
  } catch (e) {
    return message;
  }
  if (!message || message.length === 0) {
    message = 'Unknown Error Occurred !';
  }
  return message;
};

export const isObjectEmpty = object => {
  return Object.keys(object ?? {}).length === 0;
};

export const removeDuplicateObjectsFromArray = (data, key) => {
  return [...new Map(data.map(item => [key(item), item])).values()];
};

export function getTextContentFromFile(file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = event => {
      resolve(event?.target?.result);
    };
    reader.onerror = event => {
      reject(event);
    };
    reader.readAsText(file);
  });
}

export function getUserInitials(user) {
  let initials = '';
  if (user && user.first_name) {
    initials += user.first_name.slice(0, 1);
  }

  if (user && user.last_name) {
    initials += user.last_name.slice(0, 1);
  }
  return initials?.toUpperCase() ?? '';
}

export const serverToClientTime = (serverTime, withTime = true) => {
  if (withTime) {
    return moment(serverTime ?? '').format('DD/MM/YYYY, HH:mm');
  } else {
    return moment(serverTime ?? '').format('DD/MM/YYYY');
  }
};

export function showErrorNotification(msg, duration) {
  const notification = {
    msg: processErrorParent(msg),
    mode: 'error'
  };
  if (duration) notification.duration = duration;

  store.dispatch('studioToast/triggerToast', notification);
}

export function showSuccessNotification(msg, duration, details) {
  const notification = {
    msg,
    mode: 'success'
  };
  if (duration) notification.duration = duration;
  store.dispatch('studioToast/triggerToast', notification);
}

export const capitalize = str => str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);

export function updateOrgInIndexedDb(context, updateOrgObj) {
  setInIDB('currentOrg', {
    data: { ...context.rootGetters['org/getCurrentOrg'], ...updateOrgObj }
  });
}

/**
 * Gets the value of document.cookie="key=value"
 * @param {string} name
 * @returns {string | boolean} Cookie value
 */
export function getCookieValue(name) {
  const pattern = RegExp(name + '=.[^;]*');
  const matched = document.cookie.match(pattern);

  if (matched && Array.isArray(matched) && matched[0]) {
    const val = matched[0].split('=')[1];

    if (val === 'false') {
      return false;
    }

    return val;
  }

  return false;
}

export const trimLowercaseString = str => {
  return str.trim().toLowerCase();
};
